import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppThemeProvider } from './theme/ThemeContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { TTSQueueProvider } from './contexts/TTSQueueContext';
import { OfflineProvider } from './contexts/OfflineContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { OfflineIndicator, UpdatePrompt } from './components/common';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import ReadingReminderNotification from './components/ReadingReminderNotification';

// Layouts (static — always needed for structure)
import MainLayout from './components/layout/MainLayout';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./features/auth/pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./features/auth/pages/RegisterPage'));
const AuthCallbackPage = React.lazy(() => import('./features/auth/pages/AuthCallbackPage'));
const SubscriptionManager = React.lazy(
  () => import('./features/subscriptions/SubscriptionManager'),
);
const SubscriptionDetailPage = React.lazy(
  () => import('./features/subscriptions/SubscriptionDetailPage'),
);
const BlocklistManager = React.lazy(() => import('./features/blocklist/BlocklistManager'));
const SettingsManager = React.lazy(() => import('./features/settings/SettingsManager'));
const PromptManager = React.lazy(() => import('./features/settings/PromptManager'));
const NotificationManager = React.lazy(() => import('./features/settings/NotificationManager'));
const CleanupSettings = React.lazy(() => import('./features/settings/CleanupSettings'));
const ExportSettings = React.lazy(() => import('./features/settings/ExportSettings'));
const ClassificationRules = React.lazy(() => import('./features/settings/ClassificationRules'));
const NotionSettings = React.lazy(() => import('./features/settings/NotionSettings'));
const AgentTokens = React.lazy(() => import('./features/settings/AgentTokens'));
const SubscriptionReport = React.lazy(() => import('./features/reports/SubscriptionReport'));
const ArticlesFeedPage = React.lazy(() => import('./features/articles/ArticlesFeedPage'));
const AdminDashboard = React.lazy(() => import('./features/admin/AdminDashboard'));
const SharePage = React.lazy(() => import('./features/share/SharePage'));
const ReadingStatsPage = React.lazy(() => import('./features/stats/ReadingStatsPage'));
// [RSSHub disabled] 等自建 RSSHub 实例就绪后启用
// const RSSHubBrowser = React.lazy(() => import('./features/rsshub/RSSHubBrowser'));
const TagManager = React.lazy(() => import('./features/tags/TagManager'));
const OfflineArticlesPage = React.lazy(() => import('./components/OfflineArticlesList'));

const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress size={32} />
  </Box>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return user?.is_admin ? <>{children}</> : <Navigate to="/articles" />;
};

const AppRoutes: React.FC = () => {
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Global keyboard shortcut listener for ? key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Help dialog toggle
      if (e.key === '?') {
        e.preventDefault();
        setShowShortcuts((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/share/:id" element={<SharePage />} />

        {/* Admin (standalone layout) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Main tabbed layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/articles" replace />} />
          <Route path="articles" element={<ArticlesFeedPage />} />
          <Route path="subscriptions" element={<SubscriptionManager />} />
          <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
          <Route path="blocklist" element={<BlocklistManager />} />
          <Route path="settings" element={<SettingsManager />} />
          <Route path="settings/prompts" element={<PromptManager />} />
          <Route path="settings/notifications" element={<NotificationManager />} />
          <Route path="settings/cleanup" element={<CleanupSettings />} />
          <Route path="settings/export" element={<ExportSettings />} />
          <Route path="settings/notion" element={<NotionSettings />} />
          <Route path="settings/classification" element={<ClassificationRules />} />
          <Route path="settings/api-tokens" element={<AgentTokens />} />
          <Route path="reports" element={<Navigate to="/stats" replace />} />
          <Route path="reports/subscription" element={<SubscriptionReport />} />
          <Route path="stats" element={<ReadingStatsPage />} />
          {/* [RSSHub disabled] 等自建 RSSHub 实例就绪后启用
          <Route path="discover" element={<RSSHubBrowser />} />
          <Route path="rsshub" element={<Navigate to="/discover" replace />} />
          <Route path="bundles" element={<Navigate to="/discover" replace />} />
          */}
          <Route path="tags" element={<TagManager />} />
          <Route path="offline-articles" element={<OfflineArticlesPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/articles" replace />} />
      </Routes>

      {/* Global Keyboard Shortcuts Help Dialog */}
      <KeyboardShortcutsHelp open={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppThemeProvider>
        <SnackbarProvider>
          <OfflineProvider>
            <TTSQueueProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <AppRoutes />
                  {/* PWA Components */}
                  <OfflineIndicator />
                  <UpdatePrompt />
                  {/* Reading Reminder Notification */}
                  <ReadingReminderNotification />
                  {/* Sonner Toaster — 全局栈式 toast，与 useSnackbar 并存 */}
                  <Toaster
                    position="bottom-right"
                    theme="system"
                    duration={3500}
                    expand
                    richColors={false}
                    closeButton
                    toastOptions={{
                      style: {
                        fontFamily:
                          'Inter, -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif',
                        fontSize: 14,
                      },
                      className: 'plk-toast',
                    }}
                  />
                </ErrorBoundary>
              </AuthProvider>
            </TTSQueueProvider>
          </OfflineProvider>
        </SnackbarProvider>
      </AppThemeProvider>
    </BrowserRouter>
  );
};

export default App;
